import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConverterFindInterface } from '@zaoblako/shared/modules/pacs/dtos/converter/converter-find.dto';
import {
  ConverterFormDto,
  ConverterFormInterface
} from '@zaoblako/shared/modules/pacs/dtos/converter/converter-form.dto';
import { ConverterExchangeProtocolEnum } from '@zaoblako/shared/modules/pacs/enums/converter-exchange-protocol.enum';
import { ConverterReaderProtocolEnum } from '@zaoblako/shared/modules/pacs/enums/converter-reader-protocol.enum';
import { ConfirmDialogService } from '@zaoblako/web/core/services/confirm-dialog.service';
import { ConverterService } from '@zaoblako/web/core/services/control/converter.service';
import { ErrorService } from '@zaoblako/web/core/services/error.service';
import { UserPasswordService } from '@zaoblako/web/core/services/security/user-password.service';
import { FormType } from '@zaoblako/web/shared/components/form-sidebar/models/form-state';
import { FormStoreService } from '@zaoblako/web/shared/components/form-sidebar/services/form-store.service';
import { BaseFormComponent } from '@zaoblako/web/shared/dumbs/base-form/base-form.component';
import { InputTextComponent } from '@zaoblako/web/shared/dumbs/input-text/input-text.component';
import { SpinnerComponent } from '@zaoblako/web/shared/dumbs/spinner/spinner.component';
import { ConverterExchangeProtocolNamePipe } from '@zaoblako/web/shared/pipes/converter-exchange-protocol-name.pipe';
import { ConverterReaderProtocolNamePipe } from '@zaoblako/web/shared/pipes/converter-reader-protocol-name.pipe';
import { NetPingDeviceSingleSelectComponent } from '@zaoblako/web/shared/dumbs/dropdowns/net-ping-device-single-select/net-ping-device-single-select.component';
import { JoinPipe } from '@zaoblako/web/shared/pipes/join.pipe';
import { ButtonComponent } from '@zaoblako/web/shared/dumbs/button/button.component';
import { CardModule } from 'primeng/card';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectButtonModule } from 'primeng/selectbutton';
import { InputNumberModule } from 'primeng/inputnumber';
import { PasswordModule } from 'primeng/password';
import { Observable } from 'rxjs';

@Component({
  selector: 'crm-converter-form',
  standalone: true,
  imports: [
    CommonModule,
    SpinnerComponent,
    CardModule,
    InputTextComponent,
    JoinPipe,
    ButtonComponent,
    SelectButtonModule,
    FormsModule,
    CheckboxModule,
    InputNumberModule,
    PasswordModule,
    NetPingDeviceSingleSelectComponent
  ],
  templateUrl: './converter-form.component.html',
  styleUrls: ['./converter-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ConverterFormComponent extends BaseFormComponent<ConverterFormInterface> implements OnInit {
  @Input() public readonly index: number;
  public dto = ConverterFormDto;
  public converter: ConverterFormInterface = <ConverterFormInterface>{
    invertEvents: {
      controller: false,
      manualOpen: false
    }
  };

  public form$: Observable<FormType>;
  public loading$: Observable<boolean>;

  public readonly exchangeProtocolOptions: { label: string; value: ConverterExchangeProtocolEnum }[] = Object.values(
    ConverterExchangeProtocolEnum
  )
    .filter((value) => value !== ConverterExchangeProtocolEnum.TEST_MOCK_CONVERTER)
    .map((value) => ({
      label: new ConverterExchangeProtocolNamePipe().transform(value),
      value: value
    }));

  public readonly readerProtocolOptions: { label: string; value: ConverterReaderProtocolEnum }[] = Object.values(
    ConverterReaderProtocolEnum
  )
    .filter((value) => value !== ConverterReaderProtocolEnum.TEST_MOCK_READER)
    .map((value) => ({
      label: new ConverterReaderProtocolNamePipe().transform(value),
      value: value
    }));

  public constructor(
    private readonly formStoreService: FormStoreService,
    private readonly converterService: ConverterService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly confirmService: ConfirmDialogService,
    private readonly userPasswordService: UserPasswordService,
    private readonly errorService: ErrorService,
    private readonly router: Router
  ) {
    super();
  }

  public ngOnInit(): void {
    this.form$ = this.formStoreService.select((state) => state.forms[this.index]);
    this.loading$ = this.formStoreService.select((state) => state.forms[this.index]?.loading);

    if (this.formStoreService.getState.forms[this.index]?.isEdit) {
      this.getById();
    } else {
      if (this.exchangeProtocolOptions?.length === 1) {
        this.converter.exchangeProtocol = this.exchangeProtocolOptions[0].value;
      }
    }
  }

  public getById() {
    this.formStoreService.setLoading(true, this.index);

    this.converterService
      .findById<ConverterFindInterface>(this.formStoreService.getState.forms[this.index]?.entityId)
      .subscribe({
        next: (converter) => {
          this.converter = converter;
          this.formStoreService.setLoading(false, this.index);
          this.cdRef.markForCheck();
        }
      });
  }

  public saveConverter() {
    if (this.checkValidate(this.converter).valid) {
      if (this.formStoreService.getState.forms[this.index]?.entityId) {
        this.formStoreService.setLoading(true, this.index);

        this.converterService.update<ConverterFormInterface, ConverterFindInterface>(this.converter).subscribe({
          next: (converter) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage(`Конвертер "${this.converter.name}" обновлен`);
            this.formStoreService.saveNext(converter, this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      } else {
        this.formStoreService.setLoading(true, this.index);

        this.converterService.create<ConverterFindInterface>(this.converter).subscribe({
          next: (converter) => {
            this.formStoreService.setLoading(false, this.index);
            this.errorService.addSuccessMessage('Конвертер создан');
            if (this.formStoreService.getState.forms[this.index]?.isRedirectInCreate) {
              this.formStoreService.closeSidebar(this.index);
              this.router.navigate(['/control/equipment/converter/card/' + converter.id]).catch(console.error);
            } else {
              this.formStoreService.saveNext(converter, this.index);
            }
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    }
  }

  public deleteConverter() {
    this.confirmService.confirm({
      message: `Вы действительно хотите удалить конвертер "${this.converter.name}"?`,
      accept: () => {
        this.formStoreService.setLoading(true, this.index);
        this.cdRef.markForCheck();

        this.converterService.deleteById(this.formStoreService.getState.forms[this.index]?.entityId).subscribe({
          next: () => {
            this.errorService.addSuccessMessage(`Конвертер "${this.converter.name}" удален`);
            this.formStoreService.deleteNext(this.index);
          },
          error: () => {
            this.formStoreService.setLoading(false, this.index);
          }
        });
      }
    });
  }

  public generatePassword() {
    this.converter['password'] = this.userPasswordService.generatePassword();
    this.cdRef.markForCheck();
  }
}
